import {
  Combobox,
  ComboboxPopover,
  MantineColorsTuple,
  MantineThemeOverride,
  Modal,
  rem,
} from "@mantine/core";

/**
 * The app's Mantine theme.
 *
 * It lives here rather than inline in `main.tsx` so that tests can render against the same
 * one. The defaults below are not all cosmetic — the `size` middleware caps a dropdown's
 * height, and `Menu`'s overlay changes what a wheel does — so a component rendered under a
 * bare `MantineProvider` can behave differently from the same component in the app, and the
 * difference is invisible until someone reports it. `renderWithProviders` applies this;
 * `docs/scrolling.md` §5 is the case that made it worth extracting.
 */

const iOSGray: MantineColorsTuple = [
  '#F9F9FA',
  '#F2F2F7',  // systemGray6
  '#E5E5EA',  // systemGray5
  '#D1D1D6',  // systemGray4
  '#C7C7CC',  // systemGray3
  '#AEAEB2',  // systemGray2
  '#8E8E93',  // systemGray
  '#636366',  // systemGray dark
  '#48484A',  // systemGray2 dark
  '#3A3A3C',  // systemGray3 dark
  '#2C2C2E'   // systemGray4 dark
];

/**
 * How close a capped dropdown may come to the edge of the viewport. Exported because tests
 * assert against it — a literal restated in a test is a second source of truth.
 */
export const FLOATING_VIEWPORT_GUTTER = 8;

// floating UI element defaults
const floatingUIProps = {
  shadow: 'md',
  // Cap the dropdown at the space actually available in the placement
  // `flip` picked, rather than at a number chosen in advance: floating
  // ui measures it and re-measures on every scroll and resize, so a tall
  // window gets a tall menu and the cap only bites when the menu would
  // not have fitted. `padding` keeps it off the viewport edge. Passing
  // `middlewares` does not replace Mantine's defaults — `flip` and
  // `shift` are filled back in when left undefined.
  //
  // This is what makes the dropdown's `overflow-y: auto` (index.css)
  // live: without a cap it never overflows, so it never scrolls.
  middlewares: {
    size: { padding: FLOATING_VIEWPORT_GUTTER },
  },
};

// transparent overlay defaults
const transparentOverlayProps = {
  // An invisible click-catcher over the whole viewport, so a tap outside
  // dismisses the floating UI without also activating what is under it. It is
  // the one default here that changes behaviour rather than appearance:
  // being fixed, viewport-sized and overflow-less, it swallows every
  // wheel and pan and hands them to the document. `index.css` makes both
  // it and the dropdown refuse that — see docs/scrolling.md §4.
  withOverlay: true,
  overlayProps: {
    backgroundOpacity: 0,
  },
};

export const theme: MantineThemeOverride = {
  cursorType: "pointer",
  headings: {
    sizes: {
      h1: { fontSize: rem(28) },
    },
  },
  spacing: {
    xxxs: rem(2),
    xxs: rem(4),
  },
  black: "var(--plantings-text-primary-color)",
  colors: {
    gray: iOSGray,
  },
  primaryColor: "lime",
  primaryShade: 8,
  shadows: {
    xs: `0 ${rem(1)} ${rem(3)} var(--plantings-shadow-light-color)`,
    sm: `0 ${rem(1)} ${rem(3)} var(--plantings-shadow-light-color), var(--plantings-shadow-light-color) 0 ${rem(10)} ${rem(15)} ${rem(-5)}, var(--plantings-shadow-light-color) 0 ${rem(7)} ${rem(7)} ${rem(-5)}`,
    md: `0 ${rem(1)} ${rem(3)} var(--plantings-shadow-light-color), var(--plantings-shadow-light-color) 0 ${rem(20)} ${rem(25)} ${rem(-5)}, var(--plantings-shadow-light-color) 0 ${rem(10)} ${rem(10)} ${rem(-5)}`,
    lg: `0 ${rem(1)} ${rem(3)} var(--plantings-shadow-light-color), var(--plantings-shadow-light-color) 0 ${rem(28)} ${rem(23)} ${rem(-7)}, var(--plantings-shadow-light-color) 0 ${rem(12)} ${rem(12)} ${rem(-7)}`,
    xl: `0 ${rem(1)} ${rem(3)} var(--plantings-shadow-light-color), var(--plantings-shadow-light-color) 0 ${rem(36)} ${rem(28)} ${rem(-7)}, var(--plantings-shadow-light-color) 0 ${rem(17)} ${rem(17)} ${rem(-7)}`,
  },

  components: {
    // `.extend` is `identity` at runtime — it exists to type `defaultProps`
    // against the component's own props, which is why the two are imported.
    ComboboxPopover: ComboboxPopover.extend({
      defaultProps: { ...floatingUIProps },
    }),
    Select: {
      defaultProps: { ...floatingUIProps }
    },
    Combobox: Combobox.extend({
      defaultProps: { ...floatingUIProps },
    }),
    Menu: {
      defaultProps: {
        ...transparentOverlayProps,
        ...floatingUIProps
      },
    },
    Modal: Modal.extend({
      styles: {
        title: { fontWeight: 600 },
      },
      defaultProps: {
        radius: 'lg',
        overlayProps: {
          backgroundOpacity: 0.4,
        },
        // `react-remove-scroll` sets `position: relative` on `body` while a modal is open, so
        // that its scrollbar-width compensation reaches absolutely positioned children. In
        // this app that is the wrong end of the trade: it makes `body` the containing block
        // for `.contentLayer` (`position: absolute; inset: 0`), which then sizes to body's
        // *content* box — the viewport less that compensation — while the fixed chrome goes on
        // using the ICB. The panel lost ~15px off its trailing edge on every modal open, with
        // the FAB and the top bar staying exactly where they were.
        //
        // Nothing needs compensating here in the first place: `index.css` reserves the
        // scrollbar gutter permanently, so opening a modal removes no scrollbar and takes no
        // width away. The library cannot see that — it measures `innerWidth -
        // documentElement.clientWidth`, which counts a reserved gutter as removable width in
        // Safari (and reads 0 in Chrome, which is why this only reproduced in one engine).
        //
        // `noRelative` leaves `body` static, so `.contentLayer` resolves against the ICB the
        // way it does with no modal open. The unused margin still lands on `body`, where
        // nothing is laid out: every box in the shell is fixed or ICB-absolute.
        removeScrollProps: { noRelative: true },
      },
    }),
  },
};
